/**
 * "Sign in as this member": opens a title's reader site in a new tab,
 * signed in as the member for two hours (aamcirc/paywall/staff_view.py).
 * Staff see and can do what the member can, except payments, which the
 * api refuses in a staff view. Used on the member record and the support
 * desk's member panel.
 */
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { Button } from './Button'

type Options = {
  titles: { publication_slug: string; publication_name: string }[]
  refused: string | null
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.detail as { detail?: unknown } | string
    if (typeof d === 'object' && d && typeof d.detail === 'string') return d.detail
    return e.message
  }
  return 'Could not start the staff view'
}

/** `stacked` puts the title picker under the button instead of before it. */
export function StaffViewButton({ subscriberId, stacked = false }: { subscriberId: number; stacked?: boolean }) {
  const options = useQuery({
    queryKey: ['staff-view', subscriberId],
    queryFn: () => api.get<Options>(`/api/staff-view/${subscriberId}`),
    retry: false,
  })
  const [picked, setPicked] = useState<string>('')

  const start = useMutation({
    mutationFn: async (slug: string) => {
      // Open the tab inside the click so popup blockers allow it, then point
      // it at the handoff once the api has issued it.
      const tab = window.open('about:blank', '_blank')
      try {
        const { url } = await api.post<{ url: string }>(`/api/staff-view/${subscriberId}`, { publication_slug: slug })
        if (tab) {
          tab.opener = null
          tab.location.href = url
        } else {
          window.location.href = url
        }
      } catch (e) {
        tab?.close()
        throw e
      }
    },
  })

  if (options.isLoading) return null
  if (options.isError || !options.data) {
    return <span className="label text-radar-red">Staff view unavailable: {errorText(options.error)}</span>
  }
  const { titles, refused } = options.data
  const slug = picked || titles[0]?.publication_slug || ''
  const unavailable = refused ?? (titles.length === 0 ? 'no membership on any title' : null)

  const picker = titles.length > 1 && (
    <select
      aria-label="Title to view"
      className="bg-ink-800 border border-ink-500 text-paper text-[12px] px-2 py-1"
      value={slug}
      onChange={(e) => setPicked(e.target.value)}
    >
      {titles.map((t) => (
        <option key={t.publication_slug} value={t.publication_slug}>{t.publication_name}</option>
      ))}
    </select>
  )

  return (
    <div className={stacked ? 'flex flex-col items-start gap-2' : 'flex flex-wrap items-center gap-2'}>
      {!stacked && picker}
      <Button
        variant="outline"
        size="sm"
        disabled={!!unavailable || start.isPending}
        onClick={() => start.mutate(slug)}
        title={unavailable
          ? `Unavailable: ${unavailable}`
          : 'Opens the reader site in a new tab, signed in as this member for 2 hours. Payments are off.'}
      >
        {start.isPending ? 'Opening…' : titles.length === 1
          ? `Sign in as this member (${titles[0].publication_name})`
          : 'Sign in as this member'}
      </Button>
      {stacked && picker}
      {unavailable && <span className="label text-paper-trace">Unavailable: {unavailable}</span>}
      {start.isError && <span className="label text-radar-red">{errorText(start.error)}</span>}
    </div>
  )
}
